import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ComplianceBadge } from '../components/div7a/ComplianceBadge';
import { FileText, Plus, Download, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { div7aAPI } from '../lib/div7aAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { formatAUDate } from '../lib/dateUtils';

export default function Div7ALoans() {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [myrSchedule, setMyrSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    client_id: '',
    borrower_name: '',
    borrower_tfn: '',
    loan_amount: '',
    loan_type: 'unsecured_7yr',
    interest_rate: '',
    start_date: new Date().toISOString().split('T')[0]
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [loansRes, clientsRes] = await Promise.all([
        div7aAPI.listLoans(),
        clientsAPI.list()
      ]);
      setLoans(loansRes.data || []);
      setClients(clientsRes.data || []);
    } catch (error) {
      toast.error('Failed to load loans');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_id || !formData.borrower_name || !formData.loan_amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await div7aAPI.createLoan({
        client_id: formData.client_id,
        borrower_name: formData.borrower_name,
        borrower_tfn: formData.borrower_tfn || undefined,
        loan_amount: parseFloat(formData.loan_amount),
        loan_type: formData.loan_type,
        interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : undefined,
        start_date: formData.start_date
      });
      
      toast.success('Division 7A loan created successfully');
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create loan');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: '',
      borrower_name: '',
      borrower_tfn: '',
      loan_amount: '',
      loan_type: 'unsecured_7yr',
      interest_rate: '',
      start_date: new Date().toISOString().split('T')[0]
    });
  };

  const viewLoanDetails = async (loan) => {
    setSelectedLoan(loan);
    try {
      const response = await div7aAPI.getLoanMYRSchedule(loan.id);
      setMyrSchedule(response.data || []);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load MYR schedule');
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}} data-testid="loans-title">
            Division 7A Loans
          </h1>
          <p className="text-muted-foreground mt-1">Manage compliant Division 7A loan agreements and MYR schedules</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="create-loan-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Loan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Create Division 7A Loan</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="client">Client *</Label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
                    <SelectTrigger id="client" data-testid="loan-client-select">
                      <SelectValue placeholder="Select a client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="borrower_name">Borrower Name *</Label>
                  <Input
                    id="borrower_name"
                    value={formData.borrower_name}
                    onChange={(e) => setFormData({...formData, borrower_name: e.target.value})}
                    placeholder="John Smith"
                    data-testid="borrower-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="borrower_tfn">Borrower TFN</Label>
                  <Input
                    id="borrower_tfn"
                    value={formData.borrower_tfn}
                    onChange={(e) => setFormData({...formData, borrower_tfn: e.target.value})}
                    placeholder="123 456 789"
                    data-testid="borrower-tfn-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="loan_amount">Loan Amount *</Label>
                  <Input
                    id="loan_amount"
                    type="number"
                    step="0.01"
                    value={formData.loan_amount}
                    onChange={(e) => setFormData({...formData, loan_amount: e.target.value})}
                    placeholder="50000.00"
                    data-testid="loan-amount-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="loan_type">Loan Type *</Label>
                  <Select value={formData.loan_type} onValueChange={(value) => setFormData({...formData, loan_type: value})}>
                    <SelectTrigger id="loan_type" data-testid="loan-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unsecured_7yr">7-Year Unsecured</SelectItem>
                      <SelectItem value="secured_25yr">25-Year Secured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                  <Input
                    id="interest_rate"
                    type="number"
                    step="0.01"
                    value={formData.interest_rate}
                    onChange={(e) => setFormData({...formData, interest_rate: e.target.value})}
                    placeholder="4.52 (ATO benchmark)"
                    data-testid="interest-rate-input"
                  />
                  <p className="text-xs text-muted-foreground">Leave blank to use ATO benchmark rate (4.52%)</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    data-testid="start-date-input"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90"
                  disabled={submitting}
                  data-testid="submit-loan-button"
                >
                  {submitting ? 'Creating...' : 'Create Loan'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Loans Table */}
      <Card>
        <CardContent className="pt-6">
          {loans.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No Division 7A Loans Yet</h3>
              <p className="text-muted-foreground mb-4">Create your first Division 7A loan to start tracking compliance</p>
              <Button onClick={() => setShowDialog(true)} className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Create Loan
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Client</TableHead>
                    <TableHead className="font-semibold">Borrower</TableHead>
                    <TableHead className="font-semibold">Loan Amount</TableHead>
                    <TableHead className="font-semibold">Type</TableHead>
                    <TableHead className="font-semibold">Interest Rate</TableHead>
                    <TableHead className="font-semibold">Start Date</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loans.map((loan) => (
                    <TableRow key={loan.id} className="hover:bg-muted/50" data-testid={`loan-row-${loan.id}`}>
                      <TableCell className="font-medium">{loan.client_name}</TableCell>
                      <TableCell>{loan.borrower_name}</TableCell>
                      <TableCell className="font-mono">${loan.loan_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {loan.loan_type === 'unsecured_7yr' ? '7-Year Unsecured' : '25-Year Secured'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono">{loan.interest_rate}%</TableCell>
                      <TableCell>{formatAUDate(loan.start_date)}</TableCell>
                      <TableCell>
                        <Badge className={loan.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-muted text-foreground'}>
                          {loan.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => viewLoanDetails(loan)}
                          size="sm"
                          variant="outline"
                          data-testid={`view-loan-${loan.id}`}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loan Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Plus Jakarta Sans, sans-serif'}}>Loan Details</DialogTitle>
          </DialogHeader>
          {selectedLoan && (
            <div className="space-y-6 mt-4">
              {/* Loan Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Loan Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Client</p>
                      <p className="font-semibold">{selectedLoan.client_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Borrower</p>
                      <p className="font-semibold">{selectedLoan.borrower_name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Loan Amount</p>
                      <p className="font-mono font-semibold">${selectedLoan.loan_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Interest Rate</p>
                      <p className="font-mono font-semibold">{selectedLoan.interest_rate}% p.a.</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Loan Type</p>
                      <p className="font-semibold">{selectedLoan.loan_type === 'unsecured_7yr' ? '7-Year Unsecured' : '25-Year Secured'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Term</p>
                      <p className="font-semibold">{selectedLoan.term_years} years</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* MYR Schedule */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">MYR Amortization Schedule</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/50">
                          <TableHead>FY</TableHead>
                          <TableHead>MYR Amount</TableHead>
                          <TableHead>Principal</TableHead>
                          <TableHead>Interest</TableHead>
                          <TableHead>Paid</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Due Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {myrSchedule.map((schedule) => (
                          <TableRow key={schedule.id}>
                            <TableCell className="font-semibold">{schedule.financial_year}</TableCell>
                            <TableCell className="font-mono">${schedule.myr_amount.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="font-mono">${schedule.principal_component.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="font-mono">${schedule.interest_component.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="font-mono">${schedule.amount_paid.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell>
                              <Badge className={
                                schedule.status === 'paid' ? 'bg-green-100 text-green-800' :
                                schedule.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                                schedule.status === 'overdue' ? 'bg-red-100 text-red-800' :
                                'bg-blue-100 text-blue-800'
                              }>
                                {schedule.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatAUDate(schedule.due_date)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}